import Dialog from "../../../General/ConfirmableDialog";
import {
  useEffect,
  useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import type { Dayjs } from "dayjs";
import dayjs, { MOLDOVA_TIMEZONE } from "../../../../utils/dayjsTimezone";
import { Candidate } from "../types/CandidateBoardTypes";

interface ScheduleCandidateDialogProps {
  open: boolean;
  candidate: Candidate | null;
  onClose: () => void;
  onSubmit: (value: Dayjs) => void;
  isSubmitting: boolean;
  error?: string;
}

const ScheduleCandidateDialog = ({
  open,
  candidate,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}: ScheduleCandidateDialogProps) => {
  const [value, setValue] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (candidate?.scheduledFor) {
      setValue(dayjs.utc(candidate.scheduledFor).tz(MOLDOVA_TIMEZONE));
    } else if (open) {
      setValue(dayjs().tz(MOLDOVA_TIMEZONE).add(1, "hour").startOf("hour"));
    } else {
      setValue(null);
    }
  }, [candidate, open]);

  const handleSubmit = () => {
    if (!value) {
      return;
    }
    onSubmit(value);
  };

  const minValue = dayjs().tz(MOLDOVA_TIMEZONE);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {candidate
          ? `Schedule ${candidate.firstName} ${candidate.lastName}`
          : "Schedule Candidate"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Please select the date and time to schedule the interview. The time
            is in your local timezone (Moldova).
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Scheduled for"
              value={value}
              onChange={(newValue) => setValue(newValue)}
              ampm={false}
              format="DD MMM YYYY HH:mm"
              timezone={MOLDOVA_TIMEZONE}
              disablePast
              minDateTime={minValue}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!value || isSubmitting}
        >
          {isSubmitting ? "Scheduling..." : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleCandidateDialog;